<template>
  <v-layout bg-white ticketWrapper row wrap class="form">
      <v-flex col xs12>
        <v-layout row wrap pa-3>
          <v-flex xs12 lg5 :class="{'mt-3': $vuetify.breakpoint.xsOnly}">
            <span class="ticketTitleText">
              Ticket Description
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon class="titleIcons" small grey v-on="on">help_circle</v-icon>
                </template>
                Enter the type of ticket you purchased.
                Most common ticket types are General, VIP, All Access Pass,
                Reserved Seating, Multi-Day Pass, One-Day Pass, Early Bird
                Discount, Group Tickets, and Coded Discount.
              </v-tooltip>
            </span>
            <v-text-field
              class="inputText"
              :class="{
                'mr-2': $vuetify.breakpoint.mdAndUp,
              }"
              :value="ticketDescription"
              label="Ex. General Admission, VIP Admission, etc."
              name="ticket-description"
              :rules="[rules.required]"
              v-model="ticketDescription"
              outline
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm3 lg2>
            <span class="ticketTitleText">
              Ticket Cost
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon class="titleIcons" small grey v-on="on">help_circle</v-icon>
                </template>
                Make sure to enter the exact dollar amount for each ticket.
                If the ticket cost does not match your documents while filing a
                claim, you may be rejected and not receive reimbursement. The max
                amount allowed is 3000 USD per ticket.
              </v-tooltip>
            </span>
            <v-text-field
              class="inputText"
              :class="{
                'mr-2': $vuetify.breakpoint.smAndUp,
              }"
              :value="ticketCost"
              label="Ex. $49.99"
              name="ticket-cost"
              v-model="ticketCost"
              :rules="[ticketCostValidation]"
              outline
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm3 lg2>
            <span class="ticketTitleText">Quantity</span>
            <v-text-field
              class="inputText"
              :class="{
                'mr-2': $vuetify.breakpoint.smAndUp,
              }"
              :value="quantity"
              label="Ex. 1"
              name="quantity"
              v-model="quantity"
              :rules="[ticketQuantityValidation]"
              outline
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 lg3 class="ticketButtonWrapper">
            <v-btn
              class="ticketButton"
              data-testid="ticket-button"
              @click.native="calculateButtonCallback"
              :loading="quoteLoading"
              id="calculateButton"
              :disabled="!allFieldsAreFilled"
            >
              {{calculateClicked ? 'Update' : ' Calculate Protection'}}
            </v-btn>
          </v-flex>
        </v-layout>
        <v-flex class="removeIconWrapper">
          <div
            data-testid="ticket-remove-button"
            @mouseenter="showRemove = true"
            @mouseleave="showRemove = false"
            :class="showRemove ? 'removeIconContainerOpen' : 'removeIconContainer'"
            v-on:click="removeTicketCallback"
          >
            <div v-if="showRemove">
              <span
                v-if="showRemove"
                class="removeText white--text"
                :class="showRemove && 'removeTextActive'"
              >
                Remove
              </span>
              <v-icon
                id="removeButton"
                class="removeIconActive"
              >clear</v-icon>
            </div>
            <div v-else>
              <v-icon
                id="removeButton"
                class="removeIcon"
              >clear</v-icon>
            </div>
          </div>
        </v-flex>
        <v-flex
          :class="{
            'mt-4': $vuetify.breakpoint.xsOnly,
          }"
          col
          xs12>
          <span class="totalProtectionCost">
            <br v-if="screenWidth >= 375">
            Ticket Protection Cost:
            <span class="totalCostText">{{totalProtectionCost}}</span>
          </span>
        </v-flex>
      </v-flex>
    </v-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import jwtDecode from 'jwt-decode';
import urlUtils from 'utils/url';
import { getProductSlug } from 'utils/product';

export default {
  name: 'TheTicket',
  props: {
    eventName: {
      type: String,
      default: '',
    },
    eventDate: {
      type: String,
      default: '',
    },
    eventVenue: {
      type: String,
      default: '',
    },
    eventCity: {
      type: String,
      default: '',
    },
    eventCountry: {
      type: String,
      default: '',
    },
    eventState: {
      type: String,
      default: '',
    },
    isAddressValid: {
      type: Boolean,
      default: false,
    },
    removeTicket: {
      type: Function,
      default: () => {},
    },
    id: {
      type: Number,
      default: 0,
    },
    country: {
      type: String,
      default: '',
    },
    region: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      default: '',
    },
    apiKey: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    ticketDescription: '',
    ticketCost: '',
    quantity: '',
    totalProtectionCost: '',
    showTicketCostInfo: false,
    unformattedEventDate: '',
    calculateClicked: false,
    quoteLoading: false,
    rules: {
      required: value => !!value || 'Required.',
    },
    menu: false,
    items: [],
    disableCalculate: false,
    quoteData: {},
    uniqueId: '',
    quotedTickets: [],
    quantityValid: false,
    costValid: false,
    showRemove: false,
    screenWidth: '',
  }),
  mounted() {
    this.screenWidth = window.screen.width;
    this.uniqueId = this.id;
  },
  methods: {
    ...mapActions('quote', ['getInternalQuote', 'createGlobalQuotedTickets']),
    ...mapActions(['pushMessageAlert']),
    selectDate(event) {
      this.unformattedEventDate = event;
      const formattedEvent = this.$$moment.dateSlashes(event);
      this.menu = false;
      this.$emit('update:eventDate', formattedEvent);
    },
    removeTicketCallback() {
      if (this.showRemove) {
        this.removeTicket(this.uniqueId);
        this.showRemove = false;
      } else {
        this.showRemove = true;
      }
    },
    createItems() {
      const items = [];
      const formattedTicketCost = Number(this.ticketCost.replace(/[^0-9,.]/, ''));

      items.push({
        reference_id: 1, // this field is mandatory, but not used of saved for this use case
        unit_cost: formattedTicketCost,
        quantity: this.quantity,
      });

      return items;
    },
    calculateButtonCallback() {
      this.calculateClicked = true;
      this.disableCalculate = true;
      this.items = this.createItems();
      this.getQuote(this.items);
    },
    getQuote(items) {
      const locale = window.navigator.language.replace(/-/g, '_');
      const body = {
        client: urlUtils.getClientSlugFromDomain(),
        product_slug: getProductSlug(urlUtils.getProductFromDomain()),
        ip_address: this.ipStatus.ip,
        country: this.ipStatus.country_code,
        region: this.ipStatus.region_code,
        locale: locale || 'en_US',
        currency: this.currency || 'USD',
        items,
      };
      this.quoteLoading = true;

      this.getInternalQuote(body)
        .then((response) => {
          this.quoteData = response;
          this.totalProtectionCost = response.total_premium_literal;
          this.quoteLoading = false;
          this.createQuoteTickets(jwtDecode(response.token));
        })
        .catch(() => {
          this.quoteLoading = false;
          const pushMessage = 'It looks like something went wrong while quoting, please try again or email us for help at help@fanshield.com';
          this.pushMessageAlert({
            message: pushMessage,
            type: 'error',
          });
          if ('parentIFrame' in window) {
            window.parentIFrame.sendMessage({ error: pushMessage });
          }
          document.body.scrollTop = document.documentElement.scrollTop = 0; // eslint-disable-line
        });
    },
    createQuoteTickets(data) {
      this.quotedTickets = [];

      this.quotedTickets = data.qte.map(quote => ({
        currency: data.cur,
        token: this.quoteData.token,
        eventDate: this.eventDate,
        ticket: this.uniqueId,
        ticketDescription: this.ticketDescription,
        ticketCost: quote.coverage_amount,
        ticketProtectionCost: quote.premium,
        quantity: this.quantity,
      }));

      this.createGlobalQuotedTickets({
        id: this.uniqueId,
        quotedTickets: this.quotedTickets,
        globalQuotedTickets: this.globalQuotedTickets,
      });
    },
    ticketCostValidation(value) {
      let valid = true;
      let errorMessage = 'Value must be a number.';
      const number = value.substring(1, value.length);
      this.costValid = false;

      if (!Number(number)) {
        valid = false;
      } else if (Number(number) > 3000) {
        errorMessage = 'Ticket cost must be less than 3000.';
        valid = false;
      } else {
        this.costValid = true;
      }

      return valid || errorMessage;
    },
    ticketQuantityValidation(value) {
      let valid = true;
      let errorMessage = 'Value must be a whole number.';
      this.quantityValid = false;

      if (Number.isNaN(value)) {
        valid = false;
      } else if (!Number.isInteger(Number(value))) {
        valid = false;
      } else if (Number(value) > 100) {
        errorMessage = 'Ticket quantity must be less than 100.';
        valid = false;
      } else if (Number(value) <= 0) {
        errorMessage = 'Ticket quantity must be greater than 0.';
        valid = false;
      } else {
        this.quantityValid = true;
      }
      return valid || errorMessage;
    },
  },
  computed: {
    ...mapGetters('quote', ['globalQuotedTickets', 'internalQuoteLoading']),
    ...mapGetters('ipStatus', ['ipStatus']),
    formComplete() {
      let value = false;
      if (
        this.eventName
        && this.eventDate
        && this.eventVenue
        && this.eventCity
        && this.eventState
        && this.eventCountry
        && this.ticketDescription
        && this.ticketCost
        && this.quantity
      ) {
        value = true;
      }
      if (value && this.costValid && this.quantityValid) {
        this.$emit('update:eventAndTicketsComplete', true);
      } else {
        this.$emit('update:eventAndTicketsComplete', false);
      }
      return value;
    },
    allFieldsAreFilled() {
      return (this.formComplete && !this.disableCalculate && this.costValid && this.quantityValid);
    },
  },
  watch: {
    ticketCost(value) {
      if (this.ticketCost.charAt(0) !== '$') {
        this.ticketCost = `$${value}`;
      }
      if (this.calculateClicked) {
        this.disableCalculate = false;
      }
    },
    ticketDescription() {
      if (this.calculateClicked) {
        this.disableCalculate = false;
      }
    },
    quantity() {
      if (this.calculateClicked) {
        this.disableCalculate = false;
      }
    },
  },
};
</script>

<style scoped>
.appDescriptionContainer {
  padding-top: 0px;
}

.bg-white {
  background-color: #fff;
}

.removeButton {
  width: 100%;
  height: 61px;
  border-radius: 5px;
  margin: 0 auto;
  color: white;
  top: 22px;
  position: relative;
  background-color: #b7b7b7 !important;
  font-family: Gotham;
}

.ticketWrapper {
  border-radius: 5px;
  box-shadow: 1.5px 2.6px 8px 0 rgba(0, 0, 0, 0.15);
  margin: 0 24px 24px 24px;
}

.ticketTitleText {
  font-family: Gotham;
  font-size: 16px;
  color: #333;
}

.inputText {
  font-family: Gotham;
  font-size: 16px;
  color: #ccc;
}

.totalProtectionCost {
  font-family: Gotham;
  font-size: 20px;
  position: relative;
  bottom: 10px;
  left: 17px;
}

.totalCostText {
  font-weight: bold;
}

.ticketButton {
  width: 100%;
  height: 61px;
  border-radius: 5px;
  margin: 0 auto;
  background-color: #555 !important;
  color: white;
  top: 22px;
  position: relative;
}

.titleIcons {
  font-size: 16px;
  width: 0;
  position: relative;
  left: 65px;
}

.titleIcons:hover {
  cursor: pointer;
}

.removeIconWrapper {
  float: right;
  bottom: 136px;
  position: relative;
  left: 15px;
}

.removeIconContainer {
  position: relative;
  right: 25px;
  background-color: #b7b7b7;
  width: 24px;
  height: 24px;
  border-radius: 12px;
}

.removeIconContainerOpen {
  position: relative;
  right: 25px;
  background-color: #b7b7b7;
  width: 85px;
  height: 24px;
  border-radius: 12px;
  color: white;
  text-align: center;
}

.removeIconContainerOpen:hover {
  cursor: pointer;
}

.removeIcon {
  color: #fff;
  font-size: 19px;
  position: relative;
  left: 2.75px;
  top: 2.25px;
}

.removeIcon:hover {
  cursor: pointer;
}

.removeIconActive {
  color: #fff;
  font-size: 19px;
  position: relative;
  top: 2px;
}

.removeText {
  font-weight: 500;
  opacity: 0;
  height: 0;
  overflow: hidden;
}

.removeTextActive {
  opacity: 1;
  height: auto;
}

/* Tablet Break Point */
@media screen and (max-width: 1264px) {
  .description {
    width: 695px;
    height: 90px;
  }

  .removeIconWrapper {
    bottom: 245px;
  }
}

/* Mobile Break Point */
@media screen and (max-width: 767px) {
  .appDescriptionContainer {
    padding-top: 0px;
  }

  .description {
    width: 266px;
    height: 160px;
  }

  .removeButton {
    font-size: 12px;
  }

  .removeIconWrapper {
    position: relative;
    bottom: 430px;
    float: right;
    left: 20px;
  }

  .totalProtectionCost {
    font-size: 18px;
  }

  .ticketButtonWrapper {
    position: relative;
    bottom: 15px;
  }
}

/* Mobile Break Point Small */
@media screen and (max-width: 374px) {
  .totalProtectionCost {
    font-size: 16px;
  }
}
</style>
